import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircleQuestion, faFileLines, faBarsStaggered, faTag } from '@fortawesome/free-solid-svg-icons';
import { EntityColors } from '../../Themes/EntityColors';
import { EntityTypeEnum, FrontendEntityChangeResponse } from '../../Models/Models';
import CancelButton from '../Buttons/CancelButton';
import InitialsAvatar from '../Avatar/InitialsAvatar';

type ViewMode = 'date' | 'type';
type ChangeEntity = 'post' | 'page' | 'gallery' | 'language' | 'unknown';

const pageStyle = { background: '#E3F2FD', action: '#64B5F6', icon: faFileLines };
const unknownStyle = { background: '#ECEFF1', action: '#90A4AE', icon: faCircleQuestion };

const ENTITY_INFO: Record<ChangeEntity, { label: string; style: { background: string; action: string; icon: any } }> = {
    post:     { label: 'Post',    style: EntityColors.blog },
    gallery:  { label: 'Galéria', style: EntityColors.gallery },
    page:     { label: 'Stránka', style: pageStyle },
    language: { label: 'Jazyk',   style: EntityColors.language },
    unknown:  { label: 'Neznáme', style: unknownStyle },
};

function toEntity(target: EntityTypeEnum): ChangeEntity {
    switch (target) {
        case EntityTypeEnum.Page:     return 'page';
        case EntityTypeEnum.Gallery:  return 'gallery';
        case EntityTypeEnum.Post:     return 'post';
        case EntityTypeEnum.Language: return 'language';
        default:                      return 'unknown';
    }
}

function formatDateTime(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('sk-SK', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function insertedByName(value: any): string {
    if (!value) return 'Systém';
    return value.displayName ?? value.email ?? 'Neznámy';
}

interface ChangesModalProps {
    show: boolean;
    onHide: () => void;
    changes: FrontendEntityChangeResponse[];
    totalCount: number;
    isConfirming: boolean;
    onConfirm: () => void;
}

const FrontendEntityChangesModal: React.FC<ChangesModalProps> = ({
    show, onHide, changes, totalCount, isConfirming, onConfirm,
}) => {
    const [mode, setMode] = useState<ViewMode>('date');

    useEffect(() => {
        if (show) setMode('date');
    }, [show]);

    const sorted = useMemo(() => {
        return [...(changes ?? [])].sort((a, b) => new Date(b.insertedAt).getTime() - new Date(a.insertedAt).getTime());
    }, [changes]);

    const grouped = useMemo(() => {
        const map: Record<ChangeEntity, FrontendEntityChangeResponse[]> = { post: [], page: [], gallery: [], language: [], unknown: [] };
        for (const c of changes ?? []) map[toEntity(c.entityType)].push(c);
        (Object.keys(map) as ChangeEntity[]).forEach(k => {
            map[k].sort((a, b) => new Date(b.insertedAt).getTime() - new Date(a.insertedAt).getTime());
        });
        return map;
    }, [changes]);

    const renderItem = (c: FrontendEntityChangeResponse) => {
        const { label, style } = ENTITY_INFO[toEntity(c.entityType)];
        return (
            <div
                key={c.id}
                className="st-change-item"
                style={{ '--change-accent': style.action, '--change-tint': style.action + '1A' } as React.CSSProperties}
            >
                <div className="st-change-item-left">
                    <div className="st-change-icon-badge">
                        <FontAwesomeIcon icon={style.icon} className="st-change-icon" />
                    </div>
                    <div className="st-change-title-wrap">
                        <div className="st-change-title">{c.insertDescription}</div>
                        <div className="st-change-meta">
                            <span>{label} ·</span>
                            {c.insertedBy ? (
                                <InitialsAvatar
                                    name={insertedByName(c.insertedBy)}
                                    id={c.insertedBy.id}
                                    size={32}
                                    tooltipId={`change-inserted-by-${c.id}`}
                                />
                            ) : (
                                <span>Systém</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="st-change-date">
                    {formatDateTime(c.insertedAt)}
                </div>
            </div>
        );
    };

    const renderByDate = () =>
        sorted.length ? (
            <div className="st-change-list">
                {sorted.map(renderItem)}
            </div>
        ) : (
            <p className="st-change-empty">Žiadne zmeny.</p>
        );

    const renderGroup = (entity: ChangeEntity) => {
        const items = grouped[entity];
        if (!items.length) return null;
        const { label, style } = ENTITY_INFO[entity];
        return (
            <div key={entity} className="st-change-group">
                <div className="st-change-group-header">
                    <div className="st-change-group-header-left">
                        <div
                            className="st-change-icon-badge st-change-icon-badge--sm"
                            style={{ '--change-accent': style.action, '--change-tint': style.action + '1A' } as React.CSSProperties}
                        >
                            <FontAwesomeIcon icon={style.icon} className="st-change-icon" />
                        </div>
                        <span className="st-change-title">{label}</span>
                    </div>
                    <span className="st-change-count-badge">{items.length}</span>
                </div>
                <div className="st-change-list">
                    {items.map(renderItem)}
                </div>
            </div>
        );
    };

    const renderByType = () => {
        const order: ChangeEntity[] = ['post', 'page', 'gallery', 'language', 'unknown'];
        if (!order.some(k => grouped[k].length > 0)) {
            return <p className="st-change-empty">Žiadne zmeny.</p>;
        }
        return <>{order.map(renderGroup)}</>;
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <div className="st-delete-modal-stripe st-delete-modal-stripe--info" />
            <Modal.Header className="st-delete-modal-header">
                <div className="st-delete-modal-header-inner">
                    <span className="st-delete-modal-title">Zmeny</span>
                    <span className="st-change-total-badge">{totalCount}</span>
                </div>
            </Modal.Header>

            <Modal.Body className="st-help-modal-body">
                <div className="st-mode-toggle-row">
                    {([
                        { value: 'date' as ViewMode, label: 'Podľa dátumu', icon: faBarsStaggered },
                        { value: 'type' as ViewMode, label: 'Podľa typu', icon: faTag },
                    ]).map(({ value, label, icon }) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setMode(value)}
                            className={`st-mode-toggle${mode === value ? ' st-mode-toggle--active' : ''}`}
                        >
                            <FontAwesomeIcon icon={icon} className="st-mode-toggle-icon" />
                            {label}
                        </button>
                    ))}
                </div>

                <div className="st-change-scroll">
                    {mode === 'date' ? renderByDate() : renderByType()}
                </div>
            </Modal.Body>

            <Modal.Footer className="st-delete-modal-footer">
                <CancelButton onClick={onHide} disabled={isConfirming} />
                <button
                    type="button"
                    className="st-btn st-btn--success"
                    onClick={onConfirm}
                    disabled={isConfirming}
                >
                    {isConfirming ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faCheck} />}
                    <span>{isConfirming ? 'Synchronizujem...' : 'Synchronizovať'}</span>
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default FrontendEntityChangesModal;
